import { HistoryApi, CollisionSystem, HighlightAddedEntitySystem, InvalidFramePlacementSystem, MeasurementsWidgetSystem, SelectionEventSystem, PlannerCoreApi, ModelLoadedEventSystem, UnavailableProductsEventSystem, AnimationSystem, HandlePlacementValidation, HandlePlacementSystem, DraggableWhenSelectedSystem, ProductType, RemoveAutoAdaptSystem, OutlineSelectedFrameSystem, StackedFrameValidation, ConflictSystem, ConflictOutlineSystem, SuspensionRailSystem, LegsFeature, EketConnectionFittingsSystem, TvOnWallsValidation, RedOutlineSystem, HistoryEventSystem, StackableOrganizersValidation, HangingFramePlacementValidation, LightAccessoriesSystem, FrameGroupWallPlacementValidation } from '@inter-ikea-storageone/range';
import { ComponentIds, AutomaticGroupBuilderSystem } from '@inter-ikea-core/core';
import { AllowedNumberOfChildrenOfTypeValidation, AlignmentConnectionCreationSystem, FunctionalMarginSystem, FunctionalMarginValidation, OutlineSystem, PlacementZoneValidation, StandardSelectionSystem, RequiredConnectorsSatisfiedValidation, HostConnectorRotationValidation } from '@inter-ikea-core/core';
import { BestaFrameLegsFeatureSystem } from './systems/BestaFrameLegsFeatureSystem';
import { LegSelectionSystem } from './systems/LegSelectionSystem';
import { MatchDrawerColorSystem } from './systems/MatchDrawerColorSystem';
import { BestaCameraApi } from './BestaCameraApi';
import { BestaEntityApi } from './BestaEntityApi';
import { BestaProductApi } from './BestaProductApi';
import { BestaSceneApi } from './BestaSceneApi';
import { DrawerPlacementValidation } from './validations/DrawerPlacementValidation';
import { HandleOnDrawerValidation } from './validations/HandleOnDrawerValidation';
import { FrameCompartmentsSystem } from './systems/FrameCompartmentsSystem';
import { DoorRotationSystem } from './systems/DoorRotationSystem';
import { BestaOpeningTypeSystem } from './systems/BestaOpeningTypeSystem';
import { BestaConflictResolver } from './resolvers/bestaConflictResolver';
import { SimpleOutlineSelectedSystem } from '@inter-ikea-core/core-toolbox';
import { BestaLegsValidation } from './validations/BestaLegsValidation';
export class BestaCoreApi extends PlannerCoreApi {
    bootstrap(config) {
        if (!this.core) {
            throw new Error('Core not initialized yet');
        }
        // Setup core base functionality
        const { selectionService } = super.initializeBaseCoreFunctionality();
        // Register core systems
        this.registerSystem(new OutlineSystem());
        this.registerSystem(new AutomaticGroupBuilderSystem(this.core.services.entityManager));
        this.registerSystem(new SimpleOutlineSelectedSystem());
        this.registerSystem(new StandardSelectionSystem(this.core.services.entityManager, this.core.services.sceneManager));
        this.registerSystem(new FunctionalMarginSystem());
        this.registerSystem(new AlignmentConnectionCreationSystem(this.getServices()));
        // Register core validations
        this.registerValidation(new RequiredConnectorsSatisfiedValidation());
        this.registerValidation(new HostConnectorRotationValidation());
        this.registerValidation(new FunctionalMarginValidation(false, this.core.services.entityManager));
        this.registerValidation(new PlacementZoneValidation());
        const collisionValidation = this.core
            .getValidations()
            .find((validation) => validation.id === 'Collision');
        collisionValidation.enableExhaustiveValidation();
        this.registerValidation(new AllowedNumberOfChildrenOfTypeValidation(ComponentIds.DOOR_FRONT_COMPONENT_ID, ComponentIds.HANDLE_COMPONENT_ID, 1));
        // Setup plannner APIs
        this.conflictResolver = new BestaConflictResolver(this);
        this.history = new HistoryApi(this);
        this.products = new BestaProductApi(this.core.services, this.placementService, this.reportErrorToSentry);
        this.entities = new BestaEntityApi(selectionService, this.core, this.placementService, this.ipexViewer, this.reportErrorToSentry);
        // Register planner validations
        this.registerValidation(new HandleOnDrawerValidation(this));
        this.registerValidation(new HandlePlacementValidation(this));
        this.registerValidation(new DrawerPlacementValidation(this));
        this.registerValidation(new StackedFrameValidation(this));
        this.registerValidation(new StackableOrganizersValidation(this));
        this.registerValidation(new BestaLegsValidation(this));
        this.registerValidation(new TvOnWallsValidation(this));
        this.registerValidation(new HangingFramePlacementValidation(this));
        this.registerValidation(new FrameGroupWallPlacementValidation(this));
        // Register planner systems
        this.registerSystem(new OutlineSelectedFrameSystem(this));
        this.registerSystem(new ConflictSystem(this));
        this.registerSystem(new ConflictOutlineSystem(this));
        this.registerSystem(new HistoryEventSystem(this));
        this.registerSystem(new ModelLoadedEventSystem(this));
        this.registerSystem(new UnavailableProductsEventSystem(this));
        this.registerSystem(new HighlightAddedEntitySystem(this));
        this.registerSystem(new RemoveAutoAdaptSystem(this, [
            ProductType.BESTA_SHELF,
            ProductType.BESTA_GLASS_SHELF,
            ProductType.BESTA_BOTTLE_RACK
        ]));
        this.registerSystem(new InvalidFramePlacementSystem(this));
        this.registerSystem(new CollisionSystem(this));
        this.registerSystem(new MatchDrawerColorSystem(this));
        this.registerSystem(new LegSelectionSystem(this));
        this.registerSystem(new HandlePlacementSystem(this));
        this.registerSystem(new SelectionEventSystem(this));
        this.registerSystem(new LightAccessoriesSystem(this, (coreApi) => coreApi.entities.getByProductType(ProductType.BESTA_INNER_LIGHT).length > 0));
        this.registerSystem(new AnimationSystem(this));
        this.registerSystem(new DraggableWhenSelectedSystem(this, [
            ProductType.BESTA_DOOR_38_ASM,
            ProductType.BESTA_DOOR_64_ASM,
            ProductType.BESTA_DRAWER_26_ASM,
            ProductType.BESTA_DRAWER_38_ASM
        ]));
        this.registerSystem(new FrameCompartmentsSystem(this));
        this.registerSystem(new DoorRotationSystem(this));
        this.registerSystem(new BestaOpeningTypeSystem(this));
        this.registerSystem(new RedOutlineSystem(this));
        this.registerSystem(new SuspensionRailSystem(this, [
            ProductType.EKET_FRAME,
            ProductType.EKET_FRAME_WITH_GLASS_DOOR_ASM,
            ProductType.BESTA_TV_BENCH_1200_ASM,
            ProductType.BESTA_TV_BENCH_1800_ASM
        ], ProductType.BESTA_FRAME_SUSPENSION_RAIL, undefined, 'BestaSuspensionRailSystem'));
        const legsSystem = new BestaFrameLegsFeatureSystem(this);
        this.registerSystem(legsSystem);
        legsSystem.enableFeature();
        // EKET
        this.registerSystem(new EketConnectionFittingsSystem(this));
        this.registerSystem(new SuspensionRailSystem(this, [
            ProductType.BESTA_FRAME_600_ASM,
            ProductType.BESTA_FRAME_1200_ASM,
            ProductType.BESTA_TV_BENCH_1200_ASM,
            ProductType.BESTA_TV_BENCH_1800_ASM
        ], ProductType.EKET_SUSPENSION_RAIL, 2200, 'EketSuspensionRailSystem'));
        new LegsFeature(this, '70340044', 'eket');
        return this.loadProductCatalogs(config.productCatalogs);
    }
    getMainProductCatalog() {
        return this.catalogs['besta'];
    }
    /**
     *
     * @override
     */
    resetScene() {
        // Disable the system before removing all the entities as this could cause issues when enabled
        this.getSystem('BestaFrameLegsFeatureSystem')?.disable();
        super.resetScene();
        // Enable legs system again and reset the legs system state
        this.getSystem('BestaFrameLegsFeatureSystem')?.enable();
        this.getSystem('BestaFrameLegsFeatureSystem')?.reset();
    }
    /**
     *
     * @override
     */
    async loadScene(config, setRoom) {
        // We need to wait for the scene to load before loading the legs system
        // otherwise the frames will not get the correct components added.
        this.getSystem('BestaFrameLegsFeatureSystem')?.onBeforeLoad();
        this.getSystem('LegsSystem')?.disable();
        this.validations['HangingFramePlacementValidation']?.disable();
        this.validations['BestaLegsValidation']?.disable();
        await super.loadScene(config, setRoom);
        // When the scene has been loaded we can enable the legs system again¨
        this.getSystem('LegsSystem')?.enable();
        this.getSystem('BestaFrameLegsFeatureSystem')?.onAfterLoad();
        this.validations['HangingFramePlacementValidation']?.enable();
        this.validations['BestaLegsValidation']?.enable();
    }
    /**
     * @override
     */
    resetEntitiesInScene() {
        this.getSystem('BestaFrameLegsFeatureSystem')?.onBeforeLoad();
        super.resetEntitiesInScene();
        this.getSystem('BestaFrameLegsFeatureSystem')?.onAfterLoad();
    }
    initCameraApi(canvas) {
        this.camera = new BestaCameraApi(this.core.services.sceneManager, this.ipexViewer, this.core.services.entityManager, this, canvas);
        this.registerSystem(new MeasurementsWidgetSystem(this));
    }
    initSceneApi() {
        this.scene = new BestaSceneApi(this.core, this.ipexViewer, this.reportErrorToSentry);
    }
}
//# sourceMappingURL=BestaCoreApi.js.map